import { useState, useEffect, useContext } from 'react';
import { AppContext, AjaxContext, EMPTY_STRING, EMPTY_ARRAY } from 'commons-ui';
import _get from 'lodash/get';
import * as dashboardModel from '../../utils/model';

const usePriorityList = () => {
	const { getData, postData } = useContext(AjaxContext);
	const {
		showAlert,
		app: { appName }
	} = useContext(AppContext);
	const [bonusPriorityList, setBonusPriorityList]: [string[], Function] = useState([]);
	const [cashbackPriorityList, setCashbackPriorityList]: [string[], Function] = useState([]);
	const [bonusInputText, setBonusInputText] = useState(EMPTY_STRING);
	const [cashbackInputText, setCashbackInputText] = useState(EMPTY_STRING);
	const [showLoader, setShowLoader] = useState(false);

	const fetchPriorityData = async (): Promise<void> => {
		try {
			setShowLoader(true);
			const result: { data: dashboardModel.IRewardCoinsUtilResponse } = await getData({
				endpoint: '/marketing/dashboard/cashbackUtil/getCashbackUtils',
				queryParams: {
					appName
				}
			});
			if (!result?.data?.success) throw Error('Error in fetching the priority list!');
			setCashbackPriorityList(_get(result, ['data', 'cashbackSegmentPriorityList'], EMPTY_ARRAY));
			setBonusPriorityList(_get(result, ['data', 'bonusSegmentPriorityList'], EMPTY_ARRAY));
			setBonusInputText(_get(result, ['data', 'bonusSegmentPriorityList'], EMPTY_ARRAY).join(','));
			setCashbackInputText(_get(result, ['data', 'cashbackSegmentPriorityList'], EMPTY_ARRAY).join(','));
			return;
		} catch (error) {
			showAlert('Error in fetching the priority list', { type: 'error' });
			throw error;
		} finally {
			setShowLoader(false);
		}
	};

	useEffect(() => {
		fetchPriorityData();
	}, []);

	const validatePriorityList = (dataList: string): string[] => {
		return dataList
			?.split(',')
			?.filter((data: string) => data !== EMPTY_STRING)
			.map((item) => item.trim());
	};

	const submitCashbackPriority = async (): Promise<void> => {
		try {
			setShowLoader(true);
			const result: { data: dashboardModel.RESPONSE } = await postData({
				endpoint: '/marketing/dashboard/cashbackUtil/updatePriorityList',
				queryParams: {
					appName
				},
				body: {
					coinType: dashboardModel.ESupportedRewardCoinsUtil.cashbackCoins,
					priorityList: validatePriorityList(cashbackInputText)
				}
			});
			if (!result?.data?.success) throw Error('Error in updating the priority list!');
			setCashbackPriorityList(validatePriorityList(cashbackInputText));
			return;
		} catch (error) {
			showAlert('Error in updating cashback priority list', { type: 'error' });
			throw error;
		} finally {
			setShowLoader(false);
		}
	};

	const submitBonusPriority = async (): Promise<void> => {
		try {
			setShowLoader(true);
			const result: { data: dashboardModel.RESPONSE } = await postData({
				endpoint: '/marketing/dashboard/cashbackUtil/updatePriorityList',
				queryParams: {
					appName
				},
				body: {
					coinType: dashboardModel.ESupportedRewardCoinsUtil.bonusCoins,
					priorityList: validatePriorityList(bonusInputText)
				}
			});
			if (!result?.data?.success) throw Error('Error in updating the priority list!');
			setBonusPriorityList(validatePriorityList(bonusInputText));
		} catch (error) {
			showAlert('Error in updating bonus priority list', { type: 'error' });
			throw error;
		} finally {
			setShowLoader(false);
		}
	};

	const handleCashbackInputChange = (event: { target: { value: string } }) => {
		setCashbackInputText(event?.target.value);
	};

	const handleBonusInputChange = (event: { target: { value: string } }) => {
		setBonusInputText(event?.target.value);
	};

	return {
		submitCashbackPriority,
		cashbackPriorityList,
		bonusPriorityList,
		submitBonusPriority,
		bonusInputText,
		cashbackInputText,
		handleBonusInputChange,
		handleCashbackInputChange,
		showLoader
	};
};

export default usePriorityList;
